import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLeadTracking } from '../hooks/useLeadTracking';
import { usePageMeta } from '../hooks/usePageMeta';
import { TCPADisclosure } from '../components/TCPADisclosure';
import { BookOpenIcon, AwardIcon, MonitorIcon, ClipboardCheckIcon, BriefcaseIcon, LaptopIcon, CheckCircle2Icon, ChevronDownIcon, ChevronLeftIcon, LoaderIcon } from 'lucide-react';
import { useFormState } from '../hooks/useFormState';
import { formatPhone, stripPhone } from '../utils/phoneFormat';
const gtcEducationLevels = [{
  label: 'High School Diploma',
  value: '1'
}, {
  label: 'No High School Diploma',
  value: '2'
}, {
  label: 'GED',
  value: '3'
}, {
  label: '0-12 College Credits',
  value: '4'
}, {
  label: 'More Than 12 College Credits',
  value: '5'
}, {
  label: 'Associates Degree',
  value: '6'
}, {
  label: 'Bachelors Degree',
  value: '7'
}];
const gtcStartTimeframes = [{
  label: 'Immediately',
  value: '1'
}, {
  label: '1-3 Months',
  value: '2'
}, {
  label: '4-6 Months',
  value: '3'
}, {
  label: '7-12 Months',
  value: '4'
}, {
  label: 'Later Than 1 Year',
  value: '5'
}];
function getGradYearOptions(): string[] {
  const years: string[] = [];
  for (let y = 2026; y >= 1980; y--) {
    years.push(String(y));
  }
  return years;
}
const gradYearOptions = getGradYearOptions();
const gtcPrograms = [{
  slug: 'diploma-business-management',
  title: 'Diploma in Business Management',
  shortTitle: 'Business Management',
  mProgram: '63926'
}, {
  slug: 'diploma-computer-science',
  title: 'Diploma in Computer Science',
  shortTitle: 'Computer Science',
  mProgram: '63928'
}, {
  slug: 'aab-business-management',
  title: 'Associate of Applied Business (A.A.B.) in Business Management',
  shortTitle: 'A.A.B. Business Management',
  mProgram: '63927'
}, {
  slug: 'aas-computer-science',
  title: 'Associate of Applied Science (A.A.S.) in Computer Science',
  shortTitle: 'A.A.S. Computer Science',
  mProgram: '63929'
}];
export function GTCLandingPage() {
  usePageMeta('Global Tech College | Career-Focused Degree Programs', "Explore online degree programs at Global Tech College. Career-focused degree and certificate programs designed to prepare students for success in today's competitive job market.", {
    ogImage: 'https://cdn.magicpatterns.com/uploads/kMxFqqE7MbeZ2oDSzfEEA9/GTC_LOGO_(1).webp',
    ogType: 'website'
  });
  const navigate = useNavigate();
  const formRef = useRef<HTMLDivElement>(null);
  const [selectedProgram, setSelectedProgram] = useState('');
  const [formStep, setFormStep] = useState(0);
  const [zipLoading, setZipLoading] = useState(false);
  const [zipLookedUp, setZipLookedUp] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [addressError, setAddressError] = useState('');
  const [userIP, setUserIP] = useState('');
  const [urlParams] = useState<Record<string, string>>(() => {
    const params: Record<string, string> = {};
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.forEach((value, key) => {
      params[key] = value;
    });
    return params;
  });
  const {
    leadIdToken,
    trustedFormUrl
  } = useLeadTracking();
  // Fetch user IP on mount
  useEffect(() => {
    fetch('https://api.ipify.org?format=json').then(res => res.json()).then(data => setUserIP(data.ip || '')).catch(() => setUserIP(''));
  }, []);
  const {
    formData,
    errors,
    updateFormData,
    updateMultiple,
    validateUniStep1,
    validateUniStep2,
    validateUniStep3
  } = useFormState();
  const lookupZip = useCallback(async (zip: string) => {
    if (zip.length < 5) return;
    setZipLoading(true);
    try {
      const res = await fetch(`https://api.zippopotam.us/us/${zip}`);
      if (res.ok) {
        const data = await res.json();
        const place = data.places?.[0];
        if (place) {
          updateMultiple({
            city: place['place name'] || '',
            state: place['state abbreviation'] || ''
          });
          setZipLookedUp(true);
        }
      } else {
        setZipLookedUp(true);
      }
    } catch {
      setZipLookedUp(true);
    } finally {
      setZipLoading(false);
    }
  }, [updateMultiple]);
  const handleProgramChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSlug = e.target.value;
    setSelectedProgram(newSlug);
    if (newSlug && formStep === 0) {
      setFormStep(1);
    }
  };
  const handleStep1Next = () => {
    if (validateUniStep1()) setFormStep(2);
  };
  const handleStep2Next = () => {
    const zipValid = validateUniStep2();
    const addrValid = formData.address.trim().length > 0;
    if (!addrValid) {
      setAddressError('Street address is required.');
    } else {
      setAddressError('');
    }
    if (zipValid && addrValid) {
      lookupZip(formData.zipCode);
      setFormStep(3);
    }
  };
  const handleStep3Submit = async () => {
    if (validateUniStep3()) {
      setIsSubmitting(true);
      const currentProgram = gtcPrograms.find(p => p.slug === selectedProgram);
      try {
        const params = new URLSearchParams({
          ...urlParams,
          mProgram: currentProgram?.mProgram || '',
          highestEducation: formData.currentEducation,
          gradYear: formData.gradYear,
          startTimeframe: formData.startDate,
          zipCode: formData.zipCode,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: stripPhone(formData.phone),
          leadIdToken: leadIdToken,
          trustedFormUrl: trustedFormUrl,
          webLandingURL: window.location.href,
          userIP: userIP
        });
        await fetch(`https://hooks.zapier.com/hooks/catch/6973915/u7mchg3/?${params.toString()}`, {
          method: 'GET',
          mode: 'no-cors'
        });
      } catch (err) {
        console.error('Webhook submission error:', err);
      } finally {
        setIsSubmitting(false);
      }
      navigate('/thank-you', {
        state: {
          formData,
          partner: {
            name: 'Global Tech College',
            logo: 'https://cdn.magicpatterns.com/uploads/kMxFqqE7MbeZ2oDSzfEEA9/GTC_LOGO_(1).webp',
            primaryColor: '#1B365D',
            accentColor: '#8B1A1A',
            backUrl: '/colleges/global-tech-college',
            programTitle: currentProgram?.title || ''
          }
        }
      });
    }
  };
  const scrollToForm = () => {
    formRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const totalSteps = 3;
  const progressPercent = formStep === 0 ? 0 : Math.round(formStep / totalSteps * 100);
  const selectClasses = (hasError: boolean) => `block w-full px-4 py-3 text-sm border rounded-md bg-white appearance-none cursor-pointer ${hasError ? 'border-red-400' : 'border-gray-300 focus:ring-[#1B365D] focus:border-[#1B365D]'}`;
  const inputClasses = (hasError: boolean) => `block w-full px-4 py-3 text-sm border rounded-md shadow-sm ${hasError ? 'border-red-400' : 'border-gray-300 focus:ring-[#1B365D] focus:border-[#1B365D]'}`;
  const currentProgramTitle = gtcPrograms.find(p => p.slug === selectedProgram)?.title;
  const renderFormContent = () => {
    if (formStep === 0) {
      return <div className="space-y-4" data-id="element-1587" data-mp="16ho5zoclxd8d">
          <div data-id="element-1588" data-mp="16gmrtb0aoamf">
            <label htmlFor="hero-program-select" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1589" data-mp="16h5z7xsbjgm7">
              Select a Program
            </label>
            <div className="relative" data-id="element-1590" data-mp="wigsn15vhil9">
              <select id="hero-program-select" value={selectedProgram} onChange={handleProgramChange} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md bg-gray-50 appearance-none cursor-pointer focus:ring-[#1B365D] focus:border-[#1B365D] font-medium text-gray-800" data-id="element-1591" data-mp="wll14aqdf0hq">
                <option value="" data-id="element-1592" data-mp="cptfprvc23rq">-- Choose a Program --</option>
                {gtcPrograms.map(p => <option key={p.slug} value={p.slug} data-id="element-1593" data-mp="7r9vpx0av220">
                    {p.title}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500" data-id="element-1594" data-mp="3uxix1pj4xjgp">
                <ChevronDownIcon className="h-4 w-4" data-id="element-1595" data-mp="vixchm3ukwqk" />
              </div>
            </div>
          </div>
          <p className="text-xs text-gray-500 text-center" data-id="element-1596" data-mp="rgajhhvfx5rs">
            Select a program above to get started
          </p>
        </div>;
    }
    if (formStep === 1) {
      return <div className="space-y-4" data-id="element-1597" data-mp="hr0mpopniyxc">
          <div data-id="element-1598" data-mp="hpxzbqrjfem6">
            <label htmlFor="step1-program" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1599" data-mp="hqhhn8l3o50q">
              Program
            </label>
            <div className="relative" data-id="element-1600" data-mp="1af4vij3irs49">
              <select id="step1-program" value={selectedProgram} onChange={handleProgramChange} className="block w-full px-4 py-3 text-sm border border-gray-200 rounded-md bg-gray-50 appearance-none cursor-pointer font-medium text-gray-700" data-id="element-1601" data-mp="1ae3gy4naonhs">
                {gtcPrograms.map(p => <option key={p.slug} value={p.slug} data-id="element-1602" data-mp="1roqyeu1tiubl">
                    {p.title}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-400" data-id="element-1603" data-mp="1vs5d9he0t4ng">
                <ChevronDownIcon className="h-4 w-4" data-id="element-1604" data-mp="20nkrwbjur4kx" />
              </div>
            </div>
          </div>

          <div className="border-t border-gray-100 pt-3" data-id="element-1605" data-mp="25mfapdhtkt51" />

          <div data-id="element-1606" data-mp="2agt7nkc4r8bt">
            <label htmlFor="currentEducation" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1607" data-mp="2aga08xk3w2c1">
              Highest Level of Education
            </label>
            <select id="currentEducation" value={formData.currentEducation} onChange={e => updateFormData('currentEducation', e.target.value)} className={selectClasses(!!errors.currentEducation)} data-id="element-1608" data-mp="2jhreeadydty3">
              <option value="" data-id="element-1609" data-mp="1xstyfrg8yy6w">-- Please Select --</option>
              {gtcEducationLevels.map(level => <option key={level.value} value={level.value} data-id="element-1610" data-mp="22r5b09xxfmst">
                  {level.label}
                </option>)}
            </select>
            {errors.currentEducation && <p className="mt-1 text-xs text-red-600" data-id="element-1611" data-mp="2cgou9gpjxio7">
                {errors.currentEducation}
              </p>}
          </div>

          <div data-id="element-1612" data-mp="2ggyyants9z0f">
            <label htmlFor="startDate" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1613" data-mp="2ghii6a2zuxh3">
              When Would You Like to Start?
            </label>
            <select id="startDate" value={formData.startDate} onChange={e => updateFormData('startDate', e.target.value)} className={selectClasses(!!errors.startDate)} data-id="element-1614" data-mp="2qh98s0ofosbw">
              <option value="" data-id="element-1615" data-mp="w2ovi92fzz2j">-- Please Select --</option>
              {gtcStartTimeframes.map(opt => <option key={opt.value} value={opt.value} data-id="element-1616" data-mp="110hzqwk6qj06">
                  {opt.label}
                </option>)}
            </select>
            {errors.startDate && <p className="mt-1 text-xs text-red-600" data-id="element-1617" data-mp="3ecksfrxk20cf">{errors.startDate}</p>}
          </div>

          <div data-id="element-1618" data-mp="39j9tse2wcpvl">
            <label htmlFor="gradYear" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1619" data-mp="39igm9aw45wce">
              High School Graduation Year
            </label>
            <select id="gradYear" value={formData.gradYear} onChange={e => updateFormData('gradYear', e.target.value)} className={selectClasses(!!errors.gradYear)} data-id="element-1620" data-mp="30lpqz0pdyliv">
              <option value="" data-id="element-1621" data-mp="3t8ahy01y8vlw">-- Please Select --</option>
              {gradYearOptions.map(yr => <option key={yr} value={yr} data-id="element-1622" data-mp="3o8xubd397ki9">
                  {yr}
                </option>)}
            </select>
            {errors.gradYear && <p className="mt-1 text-xs text-red-600" data-id="element-1623" data-mp="3j9l0u9hvnf6p">{errors.gradYear}</p>}
          </div>

          <div className="flex gap-3 pt-2" data-id="element-1624" data-mp="2cv3ij8bgyz8c">
            <button type="button" onClick={() => setFormStep(0)} className="flex items-center justify-center px-5 py-3.5 border border-gray-300 text-sm font-bold rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-1625" data-mp="2crxuzxiho5eq">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-1626" data-mp="2ebpozx7grjpn" />
              Back
            </button>
            <button type="button" onClick={handleStep1Next} className="flex-1 bg-gradient-to-r from-[#1B365D] to-[#2A528A] hover:shadow-lg text-white font-bold py-3.5 px-6 rounded-full text-sm tracking-wider uppercase transition-all duration-200" data-id="element-1627" data-mp="20chmovg07vh8">
              Next
            </button>
          </div>
        </div>;
    }
    if (formStep === 2) {
      return <div className="space-y-4" data-id="element-1628" data-mp="1veoj8a635fkn">
          <div data-id="element-1629" data-mp="1hlqgg2rbcl72">
            <label htmlFor="zipCode" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1630" data-mp="1hm8biw28otde">
              Zip Code
            </label>
            <input type="text" id="zipCode" value={formData.zipCode} onChange={e => {
            const val = e.target.value.replace(/\D/g, '').slice(0, 5);
            updateFormData('zipCode', val);
            setZipLookedUp(false);
            if (val.length === 5) lookupZip(val);
          }} className={inputClasses(!!errors.zipCode)} placeholder="12345" maxLength={5} data-id="element-1631" data-mp="17n0wm4fuwqo6" />
            {errors.zipCode && <p className="mt-1 text-xs text-red-600" data-id="element-1632" data-mp="12yi0kon27ror">{errors.zipCode}</p>}
            {zipLoading && <div className="mt-2 flex items-center text-xs text-gray-500" data-id="element-1633" data-mp="12w5afo75eb0m">
                <LoaderIcon className="w-3 h-3 mr-1 animate-spin" data-id="element-1634" data-mp="xy3qz25t89om" />
                Looking up your location...
              </div>}
          </div>

          {zipLookedUp && <>
              <div className="grid grid-cols-2 gap-3" data-id="element-1635" data-mp="16zbovj4ez8i6">
                <div data-id="element-1636" data-mp="1lozo8et1bx39">
                  <label htmlFor="city" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1637" data-mp="1lp95ne00kvxc">
                    City
                  </label>
                  <input type="text" id="city" value={formData.city} onChange={e => updateFormData('city', e.target.value)} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-[#1B365D] focus:border-[#1B365D] bg-gray-50" placeholder="City" data-id="element-1638" data-mp="1upnuuog3mv64" />
                </div>
                <div data-id="element-1639" data-mp="3p5mlm7katn7e">
                  <label htmlFor="state" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1640" data-mp="3p54hypvb46pg">
                    State
                  </label>
                  <input type="text" id="state" value={formData.state} onChange={e => updateFormData('state', e.target.value)} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-[#1B365D] focus:border-[#1B365D] bg-gray-50" placeholder="State" data-id="element-1641" data-mp="2q1ve46hhhi5" />
                </div>
              </div>
              <div data-id="element-1642" data-mp="3b51zb1f6d995">
                <label htmlFor="address" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1643" data-mp="3b4jxzqdno4ud">
                  Street Address
                </label>
                <input type="text" id="address" value={formData.address} onChange={e => {
              updateFormData('address', e.target.value);
              if (addressError) setAddressError('');
            }} className={`block w-full px-4 py-3 text-sm border rounded-md shadow-sm ${addressError ? 'border-red-400' : 'border-gray-300 focus:ring-[#1B365D] focus:border-[#1B365D]'}`} placeholder="123 Main St" data-id="element-1644" data-mp="qeukbgooup39" />
                {addressError && <p className="mt-1 text-xs text-red-600" data-id="element-1645" data-mp="2653rzpkuasfz">{addressError}</p>}
              </div>
            </>}

          <div className="flex gap-3 pt-2" data-id="element-1646" data-mp="216apes73clul">
            <button type="button" onClick={() => setFormStep(1)} className="flex items-center justify-center px-5 py-3.5 border border-gray-300 text-sm font-bold rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-1647" data-mp="1w9agm9iux4zs">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-1648" data-mp="1n8m5bmkuliql" />
              Back
            </button>
            <button type="button" onClick={handleStep2Next} className="flex-1 bg-gradient-to-r from-[#1B365D] to-[#2A528A] hover:shadow-lg text-white font-bold py-3.5 px-6 rounded-full text-sm tracking-wider uppercase transition-all duration-200" data-id="element-1649" data-mp="1ib3s3jzsshcs">
              Next
            </button>
          </div>
        </div>;
    }
    if (formStep === 3) {
      return <div className="space-y-4" data-id="element-1650" data-mp="4drfjlup0271">
          <div className="grid grid-cols-2 gap-3" data-id="element-1651" data-mp="4esnhhgew8kj">
            <div data-id="element-1652" data-mp="3mrh3oiir5qhg">
              <label htmlFor="firstName" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1653" data-mp="3ms0bv7ilp0ia">
                First Name
              </label>
              <input type="text" id="firstName" value={formData.firstName} onChange={e => updateFormData('firstName', e.target.value)} className={inputClasses(!!errors.firstName)} placeholder="Jane" data-id="element-1654" data-mp="jaiclogw91th" />
              {errors.firstName && <p className="mt-1 text-xs text-red-600" data-id="element-1655" data-mp="9d3znyu6iv5x">{errors.firstName}</p>}
            </div>
            <div data-id="element-1656" data-mp="eavyzdx5ljk5">
              <label htmlFor="lastName" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1657" data-mp="eacfvtvrop4j">
                Last Name
              </label>
              <input type="text" id="lastName" value={formData.lastName} onChange={e => updateFormData('lastName', e.target.value)} className={inputClasses(!!errors.lastName)} placeholder="Doe" data-id="element-1658" data-mp="2z1cwy2a9pam8" />
              {errors.lastName && <p className="mt-1 text-xs text-red-600" data-id="element-1659" data-mp="2mjtsdyg1ebbk">{errors.lastName}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3" data-id="element-1660" data-mp="30hqu199cl7x3">
            <div data-id="element-1661" data-mp="30gfn50klvk1r">
              <label htmlFor="email" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1662" data-mp="2vjysromtc9g8">
                Email Address
              </label>
              <input type="email" id="email" value={formData.email} onChange={e => updateFormData('email', e.target.value)} className={inputClasses(!!errors.email)} placeholder="jane@example.com" data-id="element-1663" data-mp="27rrz4x8h2gki" />
              {errors.email && <p className="mt-1 text-xs text-red-600" data-id="element-1664" data-mp="2ct7lqtqasv50">{errors.email}</p>}
            </div>
            <div data-id="element-1665" data-mp="2crefhfjlkcxj">
              <label htmlFor="phone" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1666" data-mp="1p3d664elgsh2">
                Phone Number
              </label>
              <input type="tel" id="phone" value={formData.phone} onChange={e => updateFormData('phone', formatPhone(e.target.value))} className={inputClasses(!!errors.phone)} placeholder="(555)123-4567" maxLength={13} data-id="element-1667" data-mp="1k2h3fu5zbcd8" />
              {errors.phone && <p className="mt-1 text-xs text-red-600" data-id="element-1668" data-mp="18ivesw8o9o6p">{errors.phone}</p>}
            </div>
          </div>

          <TCPADisclosure checked={formData.tcpaConsent} onChange={checked => updateFormData('tcpaConsent', checked)} error={errors.tcpaConsent} checkboxClassName="mt-0.5 h-4 w-4 rounded border-gray-300 text-[#1B365D] focus:ring-[#1B365D] cursor-pointer flex-shrink-0" data-id="element-1669" data-mp="1dirisvhddfm9">
            By clicking the Request Info button, you agree to be contacted by
            phone, text message, or email via automated systems by Global Tech
            College about your education at the phone numbers you provided
            above, including any wireless number(s). You are not obligated to
            agree to automated contact to enroll. Note that even non-automated
            calls are recorded for quality assurance.
          </TCPADisclosure>

          <div className="flex gap-3 pt-2" data-id="element-1670" data-mp="3qqisfg6xcchj">
            <button type="button" onClick={() => setFormStep(2)} className="flex items-center justify-center px-5 py-3.5 border border-gray-300 text-sm font-bold rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-1671" data-mp="3lplbt81kbqi5">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-1672" data-mp="3bthxl3tfs1eu" />
              Back
            </button>
            <button type="button" onClick={handleStep3Submit} disabled={isSubmitting} className="flex-1 bg-gradient-to-r from-[#8B1A1A] to-[#A52A2A] hover:shadow-lg text-white font-bold py-3.5 px-6 rounded-full text-sm tracking-wider uppercase transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-1673" data-mp="d9003jhjqjbc">
              {isSubmitting && <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-1674" data-mp="w12l17180lre" />}
              {isSubmitting ? 'Submitting...' : 'Request Info'}
            </button>
          </div>
        </div>;
    }
    return null;
  };
  return <div className="min-h-screen bg-white font-sans text-gray-800" data-id="element-1675" data-mp="2hvzh6ztz50d8">
      {/* A. Custom Header */}
      <header className="bg-white py-2 sm:py-4 px-4 sm:px-12 flex items-center justify-center shadow-sm" data-id="element-1676" data-mp="2cus8myk0bx5i">
        <img src="https://cdn.magicpatterns.com/uploads/kMxFqqE7MbeZ2oDSzfEEA9/GTC_LOGO_(1).webp" alt="Global Tech College Logo" className="h-10 sm:h-16 w-auto object-contain" data-id="element-1677" data-mp="28v1b8bzz6is0" />
      </header>

      {/* B. Hero Section */}
      <section className="bg-gradient-to-br from-[#1B365D] to-[#8B1A1A] relative overflow-hidden" data-id="element-1678" data-mp="30kmjjakrnbn4">
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none bg-no-repeat bg-cover bg-center mix-blend-soft-light opacity-70" style={{
        backgroundImage: "url('https://cdn.magicpatterns.com/uploads/8uFwSQxxc9xYA37moQruEX/ChatGPT_Image_May_6,_2026,_08_20_39_PM.png')"
      }} data-id="element-1679" data-mp="2wkm5vs5hr4h4" />
        {/* Explicit grid placement rather than duplicated copy: on mobile the
            form follows the headline so it lands within reach, and the
            supporting paragraph moves below it. */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-24 grid gap-8 lg:grid-cols-2 lg:gap-x-16 lg:gap-y-6 relative z-10" data-id="element-1680" data-mp="3mvxssnvce3c7">
          {/* Headline */}
          <div className="text-white max-w-xl mx-auto lg:mx-0 text-center lg:text-left lg:col-start-1 lg:row-start-1 lg:self-end" data-id="element-1681" data-mp="ltu6ajirvy2">
            <p className="text-[#D4A843] font-medium mb-3 text-lg tracking-wide" data-id="element-1682" data-mp="4lvjdk82iwwg">
              Global Tech College Online<sup className="text-xs" data-id="element-1683" data-mp="emebt3dp7lpz">1</sup>
            </p>
            <h1 className="text-4xl sm:text-6xl lg:text-7xl font-extrabold leading-[1.05] text-white tracking-tight" data-id="element-1684" data-mp="ektg3afndwqj">
              {gtcPrograms.find(p => p.slug === selectedProgram)?.shortTitle || <>
                  Career-Focused
                  <br data-id="element-1685" data-mp="sdsvzdjcdsym" />
                  Degree Programs
                </>}
            </h1>
          </div>

          {/* Right Form */}
          <div ref={formRef} className="w-full lg:w-[520px] lg:justify-self-end bg-white rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.3)] p-6 sm:p-10 relative z-10 lg:col-start-2 lg:row-start-1 lg:row-span-2 lg:self-center" data-id="element-1686" data-mp="xc42beafhlc3">
            <form onSubmit={e => e.preventDefault()} id="gtc-lead-form" data-id="element-1687" data-mp="2pg033xxsojus">
              {/* Hidden inputs for tracking */}
              <input type="hidden" name="xxTrustedFormCertUrl" id="xxTrustedFormCertUrl" value="" data-id="element-1688" data-mp="337od9vohgy94" />
              <input type="hidden" name="leadid_token" id="leadid_token" value="" data-id="element-1689" data-mp="2ydad7fyvm2y4" />

              <h2 className="text-2xl sm:text-3xl font-bold text-[#1B365D] mb-4" data-id="element-1690" data-mp="3d26lne4uhhz9">
                Learn how we can help you succeed.
              </h2>

              {/* Progress Bar */}
              <div className="w-full bg-gray-100 rounded-full h-2 mb-8 overflow-hidden" data-id="element-1691" data-mp="3m463g8nooq39">
                <div className="bg-gradient-to-r from-[#1B365D] to-[#2A528A] h-2 rounded-full transition-all duration-500 ease-out" style={{
                width: `${progressPercent}%`
              }} data-id="element-1692" data-mp="3h6weeu47igfy"></div>
              </div>

              <AnimatePresence mode="wait" data-id="element-1693" data-mp="1mtoit08eq0t9">
                <motion.div key={formStep} initial={{
                opacity: 0,
                x: 10
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -10
              }} transition={{
                duration: 0.2
              }} data-id="element-1694" data-mp="299iho005f9c">
                  {renderFormContent()}
                </motion.div>
              </AnimatePresence>
            </form>
          </div>

          {/* Supporting copy. After the form in the markup so mobile gets it
              below; the grid puts it back under the headline on desktop. */}
          <p className="text-base lg:text-lg text-white/85 max-w-lg mx-auto lg:mx-0 text-center lg:text-left leading-relaxed lg:col-start-1 lg:row-start-2 lg:self-start" data-id="element-1695" data-mp="36pc2dii4lgdy">
            Global Tech College offers career-focused degree and certificate
            programs designed to prepare students for success in today's
            competitive job market. With flexible online options and dedicated
            support, we help you achieve your educational goals.
          </p>
        </div>
      </section>

      {/* C. Benefits Icons Row */}
      <section className="bg-gray-50 py-12 border-b border-gray-200" data-id="element-1696" data-mp="a95shepfvjc0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1697" data-mp="a7bao99gs7c5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1698" data-mp="591djt46iwnw">
            <div className="flex flex-col items-center text-center" data-id="element-1699" data-mp="187ouxiwxdty">
              <div className="w-16 h-16 bg-[#1B365D]/10 rounded-full flex items-center justify-center mb-4 text-[#1B365D]" data-id="element-1700" data-mp="1akelwuwwshk">
                <LaptopIcon className="w-8 h-8" data-id="element-1701" data-mp="3no286xd2k6t4" />
              </div>
              <h3 className="text-lg font-bold text-[#1B365D] mb-2" data-id="element-1702" data-mp="3noc0irll6q1z">
                100% Online Flexibility
              </h3>
              <p className="text-sm text-gray-600" data-id="element-1703" data-mp="3dpv0740rcz39">
                Study on your schedule with 24/7 access to coursework.
              </p>
            </div>
            <div className="flex flex-col items-center text-center" data-id="element-1704" data-mp="39ot1coar6p1t">
              <div className="w-16 h-16 bg-[#1B365D]/10 rounded-full flex items-center justify-center mb-4 text-[#1B365D]" data-id="element-1705" data-mp="1p70f7ujqvh93">
                <BriefcaseIcon className="w-8 h-8" data-id="element-1706" data-mp="1t696msrbb31h" />
              </div>
              <h3 className="text-lg font-bold text-[#1B365D] mb-2" data-id="element-1707" data-mp="1y6nfgd0qmh0k">
                Career-Focused Curriculum
              </h3>
              <p className="text-sm text-gray-600" data-id="element-1708" data-mp="235hyng2ma7l7">
                Gain practical skills designed for today's job market.
              </p>
            </div>
            <div className="flex flex-col items-center text-center" data-id="element-1709" data-mp="1agt3rys5myhw">
              <div className="w-16 h-16 bg-[#1B365D]/10 rounded-full flex items-center justify-center mb-4 text-[#1B365D]" data-id="element-1710" data-mp="1aezxwlpd8iay">
                <AwardIcon className="w-8 h-8" data-id="element-1711" data-mp="1k7nrz8oyo85z" />
              </div>
              <h3 className="text-lg font-bold text-[#1B365D] mb-2" data-id="element-1712" data-mp="1k8yuz6al1bw1">
                Accredited Institution
              </h3>
              <p className="text-sm text-gray-600" data-id="element-1713" data-mp="rqbczzqvucz6">
                Accredited by ACCET, ensuring quality education standards.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* D. Content Section */}
      <section className="py-16 bg-white" data-id="element-1714" data-mp="fo20ump71cy5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1715" data-mp="tfymq909qqm3">
          <h2 className="text-3xl font-bold text-[#1B365D] mb-6" data-id="element-1716" data-mp="ti2oolelxfjq">
            Why Choose Global Tech College?
          </h2>
          <p className="text-lg text-gray-600 mb-8 leading-relaxed" data-id="element-1717" data-mp="r176dnfz7x1">
            At Global Tech College, we are committed to providing accessible,
            high-quality education that empowers students to achieve their
            professional goals. Our programs are designed with input from
            industry experts to ensure you learn the skills employers are
            looking for right now.
          </p>
          <div className="bg-gray-50 rounded-2xl p-8 text-left border border-gray-100" data-id="element-1718" data-mp="1m3ujfdpu8iiv">
            <h3 className="text-xl font-bold text-[#1B365D] mb-4" data-id="element-1719" data-mp="1h62g7njqt9zd">
              Student Support Services
            </h3>
            <ul className="space-y-3" data-id="element-1720" data-mp="2z24ga73lon2h">
              <li className="flex items-start" data-id="element-1721" data-mp="2z5a83u3m631w">
                <CheckCircle2Icon className="w-5 h-5 text-[#8B1A1A] mr-3 flex-shrink-0 mt-0.5" data-id="element-1722" data-mp="342ip3rd1011k" />
                <span className="text-gray-700" data-id="element-1723" data-mp="2q39ct2rxxr2v">
                  Live Tutor support via email & chat
                </span>
              </li>
              <li className="flex items-start" data-id="element-1724" data-mp="2v1u9wtbwbun3">
                <CheckCircle2Icon className="w-5 h-5 text-[#8B1A1A] mr-3 flex-shrink-0 mt-0.5" data-id="element-1725" data-mp="3iqn3ij51pqej" />
                <span className="text-gray-700" data-id="element-1726" data-mp="3mt9vck1pvbbq">
                  Dedicated academic advisors to guide your journey
                </span>
              </li>
              <li className="flex items-start" data-id="element-1727" data-mp="38z95kfsqsyni">
                <CheckCircle2Icon className="w-5 h-5 text-[#8B1A1A] mr-3 flex-shrink-0 mt-0.5" data-id="element-1728" data-mp="3dzn333wpwq7a" />
                <span className="text-gray-700" data-id="element-1729" data-mp="5kd4i5s1e349">
                  Career services assistance including resume building and
                  interview prep
                </span>
              </li>
              <li className="flex items-start" data-id="element-1730" data-mp="26erjmgh4xcnj">
                <CheckCircle2Icon className="w-5 h-5 text-[#8B1A1A] mr-3 flex-shrink-0 mt-0.5" data-id="element-1731" data-mp="26gkrfz3hi2x4" />
                <span className="text-gray-700" data-id="element-1732" data-mp="21loo42ssrt4p">
                  Flexible learning with pre-recorded and live lectures
                </span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* E. Footer */}
      <footer className="bg-[#1B365D] text-white/70 py-12 text-xs" data-id="element-1733" data-mp="2pdvnzbxqei8v">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-4" data-id="element-1734" data-mp="2pazpnmhz1rjw">
          <p data-id="element-1735" data-mp="2kc6brsyrvyj7">
            Global Tech College is accredited by The Accrediting Council for
            Continuing Education & Training (ACCET).
          </p>
          <p data-id="element-1736" data-mp="2bcjd3exfh6tu">
            <sup className="text-[10px]" data-id="element-1737" data-mp="2bd1f6s6ruf9o">1</sup> Online programs are subject to
            state availability. Not all programs are available in all states.
          </p>
          <p className="pt-4 border-t border-white/10" data-id="element-1738" data-mp="zy623anrwlv8">
            © {new Date().getFullYear()} Global Tech College. All rights
            reserved.
          </p>
        </div>
      </footer>
    </div>;
}